import { searchContent } from '@echo/api-client/onearly';
import type { StrongerMemoryTypes } from '../config/environment';
import { numberChoices } from '../domain/math';
import { type ContentGraph, jsonValueOf, readGraph } from './graph';
import {
  type SmProblem,
  smPassageValueSchema,
  smPromptValueSchema,
  smSheetValueSchema,
} from './schemas';

/**
 * The programme library as Core serves it: Goodwin's workbook, one `SM Workbook` container
 * with every sheet and prompt inside, shared with everyone (docs/mvp/03-CONTENT.md).
 * Read once per day and cached; "pick for me" is computed from what the participant already
 * used, never stored as a cursor (Todd: publish "from, and in the same order as" the workbook).
 */
export interface Sheet {
  lineageUuid: string;
  stage: number;
  sheet: number;
  page: number;
  problems: SmProblem[];
  brainFact: string;
}

export interface Passage {
  lineageUuid: string;
  sequence: number;
  title: string;
  body: string;
}

export interface Prompt {
  lineageUuid: string;
  number: number;
  text: string;
}

export interface Workbook {
  sheets: Sheet[];
  prompts: Prompt[];
  /** Empty where no passages are provisioned: reading then uses the person's own material. */
  passages: Passage[];
}

export const EMPTY_WORKBOOK: Workbook = { sheets: [], prompts: [], passages: [] };

export async function loadWorkbook(types: StrongerMemoryTypes): Promise<Workbook> {
  const response = await searchContent({
    rootContentTypeUuids: [types.workbook],
    maxDepth: 1,
    first: 1,
  });
  if (response.status !== 200)
    throw new Error(`OnEarly: workbook search failed (${response.status})`);
  return workbookFromGraph(readGraph(response.data), types);
}

export function workbookFromGraph(graph: ContentGraph, types: StrongerMemoryTypes): Workbook {
  const sheets: Sheet[] = [];
  const prompts: Prompt[] = [];
  const passages: Passage[] = [];
  for (const node of graph.nodes.values()) {
    if (types.passage && node.contentTypeUuid === types.passage) {
      const parsed = smPassageValueSchema.safeParse(jsonValueOf(node));
      if (parsed.success) {
        const { title, body, sequence } = parsed.data;
        passages.push({ lineageUuid: node.lineageUuid, title, body, sequence });
      }
      continue;
    }
    if (node.contentTypeUuid === types.sheet) {
      const parsed = smSheetValueSchema.safeParse(jsonValueOf(node));
      if (parsed.success) sheets.push({ lineageUuid: node.lineageUuid, ...parsed.data });
    } else if (node.contentTypeUuid === types.prompt) {
      const parsed = smPromptValueSchema.safeParse(jsonValueOf(node));
      if (parsed.success) prompts.push({ lineageUuid: node.lineageUuid, ...parsed.data });
    }
  }
  sheets.sort((x, y) => x.stage - y.stage || x.sheet - y.sheet);
  prompts.sort((x, y) => x.number - y.number);
  passages.sort((x, y) => x.sequence - y.sequence);
  return { sheets, prompts, passages };
}

/**
 * "Pick for me": the first item in printed order the participant has no attempt for. Past
 * the end of the workbook it starts over — the programme is meant to be repeated.
 */
export function nextUnused<T extends { lineageUuid: string }>(
  items: readonly T[],
  used: ReadonlySet<string>,
): T | undefined {
  return items.find((item) => !used.has(item.lineageUuid)) ?? items[0];
}

/** The `count` items from `first` onwards in printed order, for the chooser. */
export function fromItem<T extends { lineageUuid: string }>(
  items: readonly T[],
  first: T | undefined,
  count: number,
): T[] {
  if (!first) return items.slice(0, count);
  const start = items.findIndex((item) => item.lineageUuid === first.lineageUuid);
  return items.slice(Math.max(0, start), Math.max(0, start) + count);
}

/**
 * Four answer choices for a workbook problem: the answer plus three nearby distractors, in
 * a stable order derived from the problem itself so a sheet looks the same every time. The
 * workbook does not grade; choices are a touch-friendly way to move on, not a test.
 */
export function mathChoices(problem: SmProblem): number[] {
  return numberChoices(
    problem.answer,
    problem.a * 1_000 + problem.b * 7 + (problem.op === '+' ? 1 : 2),
  );
}
